import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/Auth';
import { adminService, WaitlistEntry, WaitlistAnalytics } from '../../services/adminService';
import { getApiUrl } from '../../config/api';
import { isAdminUser } from '../../config/environment';
import UserPresenceManager from './UserPresenceManager';
import PointsAnalytics from './PointsAnalytics';
import UserPointsManager from './UserPointsManager';
import VeoCreditAdminManager from './VeoCreditAdminManager';
import OverviewDashboard from './OverviewDashboard';
import styles from './FuturisticAdmin.module.css';

// User Usage interfaces
interface UserUsageData {
  plan: string;
  billingPeriod: { start: string; end: string };
  usage: {
    captions: { used: number; limit: number | 'unlimited'; remaining: number | 'unlimited' };
    thumbnails: { used: number; limit: number | 'unlimited'; remaining: number | 'unlimited' };
    audioClips: { used: number; limit: number | 'unlimited'; remaining: number | 'unlimited' };
    voiceSynthesis: { used: number; limit: number | 'unlimited'; remaining: number | 'unlimited' };
    musicGeneration: { used: number; limit: number | 'unlimited'; remaining: number | 'unlimited' };
    titleDescriptions: { used: number; limit: number | 'unlimited'; remaining: number | 'unlimited' };
    miniClips: { used: number; limit: number | 'unlimited'; remaining: number | 'unlimited' };
    prompts: { used: number; limit: number | 'unlimited'; remaining: number | 'unlimited' };
    scripts: { used: number; limit: number | 'unlimited'; remaining: number | 'unlimited' };
    imageAnalyses: { used: number; limit: number | 'unlimited'; remaining: number | 'unlimited' };
    imagesGenerated: { used: number; limit: number | 'unlimited'; remaining: number | 'unlimited' };
    videosGenerated: { used: number; limit: number | 'unlimited'; remaining: number | 'unlimited' };
  };
  creditBalance: number;
  individualAppSubscriptions: {
    caption: boolean;
    thumbnail: boolean;
    audio: boolean;
    title: boolean;
    prompt: boolean;
    imageAnalysis: boolean;
  };
}

interface User {
  id: string;
  email: string;
  plan: string;
  role: string;
  createdAt: string;
  updatedAt?: string;
  isVerified: boolean;
  planUpdatedAt?: string;
  monthlyPoints?: number;
  pointsUsed?: number;
  pointsRemaining?: number;
  isUnlimited?: boolean;
  videoType?: string;
}

interface UsersResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

type ActiveTab = 'overview' | 'waitlist' | 'users' | 'user-usage' | 'presence' | 'points-system' | 'veo-credits' | 'settings';

const FuturisticWaitlistManager: React.FC = () => {
  const { user, logout: authLogout } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [analytics, setAnalytics] = useState<WaitlistAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // User Usage state
  const [users, setUsers] = useState<UsersResponse | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userUsage, setUserUsage] = useState<UserUsageData | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [usagePage, setUsagePage] = useState(1);

  useEffect(() => {
    if (activeTab === 'waitlist') {
      loadWaitlistData();
      loadAnalytics();
    } else if (activeTab === 'user-usage') {
      loadUsers(1);
    } else if (activeTab === 'points-system') {
      // TODO: Load points system data in Phase 2
      console.log('Points System tab activated - data loading will be implemented in Phase 2');
    }
  }, [currentPage, activeTab, usagePage]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  };

  // Load users for user usage tab
  const loadUsers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(`/admin/users?page=${page}&limit=20`), {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) throw new Error('Failed to load users');
      const data = await response.json();
      setUsers(data);
      setUsagePage(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Load user usage data
  const loadUserUsage = async (userId: string) => {
    try {
      setUsageLoading(true);
      
      // Fetch both usage and points data in parallel
      const [usageResponse, pointsResponse] = await Promise.all([
        fetch(getApiUrl(`/admin/users/${userId}/usage`), {
          headers: getAuthHeaders(),
        }),
        fetch(getApiUrl(`/admin/users/${userId}/points`), {
          headers: getAuthHeaders(),
        })
      ]);
      
      if (!usageResponse.ok) {
        throw new Error('Failed to load user usage data');
      }
      
      const usageData = await usageResponse.json();
      setUserUsage(usageData);
      
      // Update selected user with points data if available
      if (pointsResponse.ok) {
        const pointsData = await pointsResponse.json();
        const balance = pointsData.balance || {};
        const isPointsEnabled = pointsData.isPointsEnabled;
        
        console.log('Points data:', pointsData); // Debug log
        
        setSelectedUser(prev => prev ? {
          ...prev,
          monthlyPoints: isPointsEnabled ? (balance.monthlyPoints || 0) : undefined,
          pointsUsed: isPointsEnabled ? (balance.pointsUsed || 0) : undefined,
          pointsRemaining: isPointsEnabled ? (balance.pointsRemaining || 0) : undefined,
          isUnlimited: isPointsEnabled ? (balance.isUnlimited || false) : undefined,
        } : prev);
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user usage');
      setUserUsage(null);
    } finally {
      setUsageLoading(false);
    }
  };

  const handleUserSelect = (selectedUser: User) => {
    setSelectedUser(selectedUser);
    loadUserUsage(selectedUser.id);
  };

  const filteredUsers = users?.users.filter(u => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.plan.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const getUsagePercentage = (used: number, limit: number | 'unlimited'): number => {
    if (limit === 'unlimited') return 0;
    if (typeof limit !== 'number' || limit <= 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number): string => {
    if (percentage >= 90) return '#ef4444';
    if (percentage >= 75) return '#f59e0b';
    if (percentage >= 50) return '#3b82f6';
    return '#10b981';
  };

  const loadWaitlistData = async () => {
    try {
      setLoading(true);
      const response = await adminService.getWaitlistEntries(currentPage, 10);
      setEntries(response.data);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Failed to load waitlist data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const analyticsData = await adminService.getWaitlistAnalytics();
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('dev_authenticated');
    authLogout();
    window.location.href = '/';
  };

  const handleExport = async () => {
    try {
      const blob = await adminService.exportWaitlist();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'waitlist-export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const convertToCSV = (data: WaitlistEntry[]) => {
    const headers = ['Email', 'Source', 'Location', 'Date', 'Time', 'Status'];
    const rows = data.map(entry => {
      const location = [entry.country, entry.city].filter(Boolean).join(', ') || '';
      const date = new Date(entry.createdAt);
      return [
        entry.email,
        entry.source,
        location,
        date.toLocaleDateString(),
        date.toLocaleTimeString(),
        entry.isConverted ? 'Converted' : 'Active'
      ];
    });
    
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const downloadCSV = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const navigationItems = [
    {
      id: 'overview' as ActiveTab,
      label: 'Overview',
      icon: '📊',
      description: 'System metrics'
    },
    {
      id: 'waitlist' as ActiveTab,
      label: 'Waitlist',
      icon: '📧',
      description: 'Email signups'
    },
    {
      id: 'users' as ActiveTab,
      label: 'Users',
      icon: '👥',
      description: 'User management'
    },
    {
      id: 'user-usage' as ActiveTab,
      label: 'User Usage',
      icon: '📈',
      description: 'AI usage analytics'
    },
    {
      id: 'points-system' as ActiveTab,
      label: 'Points System',
      icon: '🪙',
      description: 'Points management'
    },
    {
      id: 'veo-credits' as ActiveTab,
      label: 'VEO Credits',
      icon: '🎬',
      description: 'VEO credit management'
    },
    {
      id: 'presence' as ActiveTab,
      label: 'User Presence',
      icon: '👀',
      description: 'Live user activity'
    },
    {
      id: 'settings' as ActiveTab,
      label: 'Settings',
      icon: '⚙️',
      description: 'System config'
    }
  ];

  const renderWaitlistContent = () => (
    <div style={{ color: 'white', padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 className={styles.testTitle}>📧 Waitlist Management</h1>
        <p style={{ color: '#06b6d4', fontSize: '1.1rem', marginBottom: '2rem' }}>
          Manage your soft-launch email signups
        </p>
        
        {/* Analytics Cards */}
        {analytics && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            <div className={styles.testCard} style={{ textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>👥</span>
                <span style={{ color: '#06b6d4', fontWeight: 'bold' }}>Total Signups</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{analytics.totalSignups}</div>
            </div>
            
            <div className={styles.testCard} style={{ textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>📈</span>
                <span style={{ color: '#a855f7', fontWeight: 'bold' }}>Growth Rate</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{analytics.growthRate}%</div>
            </div>
            
            <div className={styles.testCard} style={{ textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>🎯</span>
                <span style={{ color: '#10b981', fontWeight: 'bold' }}>Conversion Rate</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{analytics.conversionRate.toFixed(1)}%</div>
            </div>
          </div>
        )}

        

        {/* Export Button */}
        <div style={{ marginBottom: '1.5rem' }}>
          <button 
            onClick={handleExport}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #06b6d4 0%, #a855f7 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Entries Table */}
      <div className={styles.testCard} style={{ textAlign: 'left', padding: '1.5rem' }}>
        <h3 style={{ marginBottom: '1rem', color: '#06b6d4' }}>Email Entries</h3>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
            <p>Loading waitlist data...</p>
          </div>
        ) : entries.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(6, 182, 212, 0.3)' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#06b6d4' }}>Email</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#06b6d4' }}>Source</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#06b6d4' }}>Location</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#06b6d4' }}>Date</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#06b6d4' }}>Time</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#06b6d4' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, index) => {
                    const location = [entry.country, entry.region, entry.city].filter(Boolean).join(', ');
                    const date = new Date(entry.createdAt);
                    
                    return (
                      <tr key={entry.id} style={{ borderBottom: '1px solid rgba(71, 85, 105, 0.3)' }}>
                        <td style={{ padding: '0.75rem' }}>{entry.email}</td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{ 
                            padding: '0.25rem 0.5rem', 
                            background: 'rgba(6, 182, 212, 0.2)', 
                            borderRadius: '0.25rem',
                            fontSize: '0.875rem'
                          }}>
                            {entry.source}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', color: '#9ca3af', fontSize: '0.875rem', maxWidth: '150px', overflow:'hidden', whiteSpace: 'nowrap', textOverflow:'ellipsis' }} title={location}>
                          {location ? (
                            <span style={{ 
                              padding: '0.2rem 0.4rem', 
                              background: 'rgba(16, 185, 129, 0.2)', 
                              borderRadius: '0.25rem',
                              fontSize: '0.8rem'
                            }}>
                              🌍 {location}
                            </span>
                          ) : (
                            <span style={{ color: '#6b7280' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '0.75rem', color: '#9ca3af' }}>
                          {date.toLocaleDateString()}
                        </td>
                        <td style={{ padding: '0.75rem', color: '#9ca3af', fontSize: '0.875rem' }}>
                          <span style={{ 
                            padding: '0.2rem 0.4rem', 
                            background: 'rgba(168, 85, 247, 0.2)', 
                            borderRadius: '0.25rem',
                            fontSize: '0.8rem'
                          }}>
                            🕐 {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{ 
                            padding: '0.25rem 0.5rem', 
                            background: entry.isConverted ? 'rgba(16, 185, 129, 0.2)' : 'rgba(168, 85, 247, 0.2)', 
                            color: entry.isConverted ? '#10b981' : '#a855f7',
                            borderRadius: '0.25rem',
                            fontSize: '0.875rem'
                          }}>
                            {entry.isConverted ? '✅ Converted' : '⏳ Active'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'center', marginTop: '1.5rem', gap: '1rem' }}>
              <div style={{ color: '#9ca3af' }}>
                Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, total)} of {total} entries
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem 1rem',
                    background: currentPage === 1 ? 'rgba(71, 85, 105, 0.3)' : 'rgba(6, 182, 212, 0.2)',
                    color: currentPage === 1 ? '#6b7280' : '#06b6d4',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>
                <span style={{ padding: '0.5rem 1rem', color: '#9ca3af' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.5rem 1rem',
                    background: currentPage === totalPages ? 'rgba(71, 85, 105, 0.3)' : 'rgba(6, 182, 212, 0.2)',
                    color: currentPage === totalPages ? '#6b7280' : '#06b6d4',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>No waitlist entries yet</p>
            <p style={{ color: '#9ca3af' }}>
              Signups from your soft-launch page will appear here
            </p>
          </div>
        )}
      </div>

      {/* Location Analytics */}
      {analytics?.locationBreakdown && (
          <div className={styles.testCard} style={{ textAlign: 'left', padding: '1.5rem', marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem', color: '#06b6d4' }}>🌍 Geographic Distribution</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              
              {/* Top Countries */}
              <div>
                <h4 style={{ color: '#10b981', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Top Countries</h4>
                {Object.entries(analytics.locationBreakdown.countries)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 5)
                  .map(([country, count]) => (
                    <div key={country} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', fontSize: '0.85rem' }}>
                      <span>🌍 {country}</span>
                      <span style={{ color: '#06b6d4' }}>{count}</span>
                    </div>
                  ))}
              </div>

              {/* Top Cities */}
              <div>
                <h4 style={{ color: '#a855f7', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Top Cities</h4>
                {Object.entries(analytics.locationBreakdown.cities)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 5)
                  .map(([city, count]) => (
                    <div key={city} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', fontSize: '0.85rem' }}>
                      <span>🏙️ {city}</span>
                      <span style={{ color: '#06b6d4' }}>{count}</span>
                    </div>
                  ))}
              </div>

              {/* Top Locations (Combined) */}
              <div>
                <h4 style={{ color: '#f59e0b', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Popular Locations</h4>
                {entries.slice(0, 5).map((entry, idx) => {
                  const location = [entry.country, entry.city].filter(Boolean).join(', ');
                  return location ? (
                    <div key={idx} style={{ padding: '0.3rem 0', fontSize: '0.85rem' }}>
                      <span>📍 {location}</span>
                    </div>
                  ) : null;
                }).filter(Boolean).slice(0, 5)}
              </div>
              
            </div>
          </div>
        )}
    </div>
  );

  const renderUserUsageContent = () => (
    <div style={{ color: 'white', padding: '2rem', height: '100%' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 className={styles.testTitle}>📈 User Usage Analytics</h1>
        <p style={{ color: '#06b6d4', fontSize: '1.1rem', marginBottom: '2rem' }}>
          Monitor AI usage across all features
        </p>

        {error && (
          <div style={{ 
            padding: '1rem', 
            background: 'rgba(239, 68, 68, 0.2)', 
            border: '1px solid rgba(239, 68, 68, 0.3)', 
            borderRadius: '0.5rem',
            marginBottom: '2rem'
          }}>
            <p style={{ color: '#f87171' }}>{error}</p>
            <button 
              onClick={() => setError('')}
              style={{ 
                marginTop: '0.5rem', 
                color: '#fca5a5', 
                background: 'none', 
                border: 'none', 
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              Dismiss
            </button>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', height: 'calc(100vh - 300px)' }}>
          {/* Users List */}
          <div className={styles.testCard} style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #333' }}>
              <h3 style={{ marginBottom: '1rem', color: '#06b6d4' }}>Users ({users?.total || 0})</h3>
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(30, 41, 59, 0.5)',
                  border: '1px solid rgba(71, 85, 105, 0.5)',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.9rem'
                }}
              />
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
                  <p>Loading users...</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {filteredUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => handleUserSelect(u)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        border: selectedUser?.id === u.id 
                          ? '1px solid rgba(6, 182, 212, 0.3)' 
                          : '1px solid transparent',
                        background: selectedUser?.id === u.id 
                          ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%)'
                          : 'rgba(30, 41, 59, 0.3)',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedUser?.id !== u.id) {
                          e.currentTarget.style.background = 'rgba(71, 85, 105, 0.5)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedUser?.id !== u.id) {
                          e.currentTarget.style.background = 'rgba(30, 41, 59, 0.3)';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <p style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                            {u.email}
                          </p>
                          <p style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                            {u.plan.toUpperCase()} • {new Date(u.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          background: u.plan === 'creator_unlimited' ? 'rgba(168, 85, 247, 0.2)' :
                                     u.plan === 'creator_studio' ? 'rgba(139, 92, 246, 0.2)' :
                                     u.plan === 'creator_pro' ? 'rgba(59, 130, 246, 0.2)' :
                                     u.plan === 'creator_lite' ? 'rgba(16, 185, 129, 0.2)' :
                                     'rgba(107, 114, 128, 0.2)',
                          color: u.plan === 'creator_unlimited' ? '#a855f7' :
                                 u.plan === 'creator_studio' ? '#8b5cf6' :
                                 u.plan === 'creator_pro' ? '#3b82f6' :
                                 u.plan === 'creator_lite' ? '#10b981' :
                                 '#6b7280'
                        }}>
                          {u.plan}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Usage Details */}
          <div className={styles.testCard} style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #333' }}>
              {selectedUser ? (
                <div>
                  <h3 style={{ color: '#06b6d4', fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                    {selectedUser.email}
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.9rem', color: '#9ca3af', marginBottom: '0.75rem' }}>
                    <span>Plan: {selectedUser.plan.toUpperCase()}</span>
                    <span>•</span>
                    <span>Joined: {new Date(selectedUser.createdAt).toLocaleDateString()}</span>
                    {selectedUser.updatedAt && (
                      <>
                        <span>•</span>
                        <span>Updated: {new Date(selectedUser.updatedAt).toLocaleDateString()}</span>
                      </>
                    )}
                    {selectedUser.videoType && (
                      <>
                        <span>•</span>
                        <span>Video Type: {selectedUser.videoType}</span>
                      </>
                    )}
                  </div>
                  
                  {/* Points Display */}
                  {selectedUser.monthlyPoints !== undefined && (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '1rem',
                      padding: '0.75rem',
                      background: 'rgba(6, 182, 212, 0.1)',
                      border: '1px solid rgba(6, 182, 212, 0.3)',
                      borderRadius: '0.5rem',
                      fontSize: '0.85rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1.2rem' }}>🎯</span>
                        <span style={{ color: '#06b6d4', fontWeight: 'bold' }}>Points:</span>
                      </div>
                      {selectedUser.isUnlimited ? (
                        <span style={{ color: '#10b981', fontWeight: 'bold' }}>Unlimited ∞</span>
                      ) : (
                        <>
                          <span style={{ color: 'white' }}>
                            <strong>{selectedUser.pointsRemaining || 0}</strong> / {selectedUser.monthlyPoints || 0}
                          </span>
                          <span style={{ color: '#9ca3af' }}>
                            ({selectedUser.pointsUsed || 0} used)
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <h3 style={{ color: '#06b6d4', fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                    Select a User
                  </h3>
                  <p style={{ fontSize: '0.9rem', color: '#9ca3af' }}>
                    Choose a user from the list to view their usage analytics
                  </p>
                </div>
              )}
            </div>

            <div style={{ flex: 1, padding: '1.5rem', overflowY: 'auto' }}>
              {usageLoading ? (
                <div style={{ textAlign: 'center', paddingTop: '3rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
                  <p>Loading usage data...</p>
                </div>
              ) : userUsage ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {/* Usage Cards */}
                  {[
                    { label: 'Video Captions', used: userUsage.usage.captions?.used || 0, limit: userUsage.usage.captions?.limit || 0, icon: '📝' },
                    { label: 'AI Prompts', used: userUsage.usage.prompts?.used || 0, limit: userUsage.usage.prompts?.limit || 0, icon: '💡' },
                    { label: 'AI Scripts', used: userUsage.usage.scripts?.used || 0, limit: userUsage.usage.scripts?.limit || 0, icon: '📋' },
                    { label: 'Images Generated', used: userUsage.usage.imagesGenerated?.used || 0, limit: userUsage.usage.imagesGenerated?.limit || 0, icon: '🖼️' },
                    { label: 'Videos Generated', used: userUsage.usage.videosGenerated?.used || 0, limit: userUsage.usage.videosGenerated?.limit || 0, icon: '🎬' },
                    { label: 'Music Generation', used: userUsage.usage.musicGeneration?.used || 0, limit: userUsage.usage.musicGeneration?.limit || 0, icon: '🎵' },
                    { label: 'Voice Synthesis', used: userUsage.usage.voiceSynthesis?.used || 0, limit: userUsage.usage.voiceSynthesis?.limit || 0, icon: '🎤' },
                    { label: 'Image Analysis', used: userUsage.usage.imageAnalyses?.used || 0, limit: userUsage.usage.imageAnalyses?.limit || 0, icon: '🔍' }
                  ].map((item) => {
                    const percentage = getUsagePercentage(item.used, item.limit);
                    const color = getUsageColor(percentage);
                    const isUnlimited = item.limit === 'unlimited';

                    return (
                      <div key={item.label} style={{
                        background: 'rgba(30, 41, 59, 0.5)',
                        border: '1px solid rgba(71, 85, 105, 0.3)',
                        borderRadius: '0.5rem',
                        padding: '1rem'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <span style={{ fontSize: '1.5rem' }}>{item.icon}</span>
                            <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{item.label}</span>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            {isUnlimited ? (
                              <span style={{ color: '#06b6d4', fontWeight: 'bold', fontSize: '1.2rem' }}>∞</span>
                            ) : (
                              <div style={{ fontSize: '0.9rem' }}>
                                <span style={{ fontWeight: 'bold' }}>{item.used}</span>
                                <span style={{ color: '#9ca3af' }}>/{item.limit}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {!isUnlimited && (
                          <div>
                            <div style={{
                              height: '0.5rem',
                              background: '#374151',
                              borderRadius: '9999px',
                              overflow: 'hidden',
                              marginBottom: '0.5rem'
                            }}>
                              <div style={{
                                width: `${percentage}%`,
                                height: '100%',
                                background: color,
                                borderRadius: '9999px',
                                transition: 'width 0.3s'
                              }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#9ca3af' }}>
                              <span>{percentage.toFixed(0)}% used</span>
                              <span>{typeof item.limit === 'number' ? item.limit - item.used : 0} remaining</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Credit Balance for Ministry Pro */}
                  {userUsage.plan === 'ministry_pro' && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
                      border: '1px solid rgba(168, 85, 247, 0.3)',
                      borderRadius: '0.5rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>💎</span>
                        <span style={{ color: '#a855f7', fontWeight: 'bold' }}>Credit Balance</span>
                      </div>
                      <p style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {userUsage.creditBalance || 0}
                      </p>
                      <p style={{ fontSize: '0.8rem', color: '#c084fc' }}>
                        Available credits for overage usage
                      </p>
                    </div>
                  )}
                </div>
              ) : selectedUser ? (
                <div style={{ textAlign: 'center', paddingTop: '3rem' }}>
                  <span style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem' }}>⚠️</span>
                  <p style={{ marginBottom: '0.5rem' }}>Unable to load usage data for this user</p>
                  <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                    This might be because they haven't used any AI features yet
                  </p>
                </div>
              ) : (
                <div style={{ textAlign: 'center', paddingTop: '3rem' }}>
                  <span style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem' }}>👤</span>
                  <p style={{ marginBottom: '0.5rem' }}>No user selected</p>
                  <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                    Select a user from the list to view their AI usage analytics
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'waitlist':
        return renderWaitlistContent();
      case 'overview':
        return <OverviewDashboard />;
      case 'users':
        return (
          <div style={{ color: 'white', textAlign: 'center', padding: '4rem' }}>
            <h1 className={styles.testTitle}>👥 User Management</h1>
            <p style={{ color: '#06b6d4', fontSize: '1.1rem' }}>Coming soon...</p>
          </div>
        );
      case 'user-usage':
        return renderUserUsageContent();
      case 'points-system':
        return (
          <div style={{ color: 'white', padding: '1rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <h1 className={styles.testTitle}>🪙 Points System Management</h1>
              <p style={{ color: '#06b6d4', fontSize: '1.1rem' }}>
                Manage user points, allocations, and system analytics
              </p>
            </div>

            {/* Error Display */}
            {error && (
              <div style={{ 
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                color: '#fca5a5'
              }}>
                ⚠️ {error}
              </div>
            )}

            {/* Main Content Tabs */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '1.5rem',
            }}>
              <div style={{ 
                background: 'rgba(15, 23, 42, 0.8)', 
                border: '1px solid rgba(71, 85, 105, 0.3)',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                <PointsAnalytics onError={setError} />
              </div>

              <div style={{ 
                background: 'rgba(15, 23, 42, 0.8)', 
                border: '1px solid rgba(71, 85, 105, 0.3)',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                <UserPointsManager onError={setError} />
              </div>
            </div>
          </div>
        );
      case 'veo-credits':
        return <VeoCreditAdminManager />;
      case 'presence':
        return <UserPresenceManager />;
      case 'settings':
        return (
          <div style={{ color: 'white', textAlign: 'center', padding: '4rem' }}>
            <h1 className={styles.testTitle}>⚙️ Settings</h1>
            <p style={{ color: '#06b6d4', fontSize: '1.1rem' }}>Coming soon...</p>
          </div>
        );
      default:
        return null;
    }
  };

  // SECURITY CHECK: Only allow admin users (after all hooks)
  if (!user || !isAdminUser(user)) {
    return (
      <div className={styles.container}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh',
          color: 'white',
          textAlign: 'center'
        }}>
          <div style={{ 
            background: 'rgba(15, 23, 42, 0.8)', 
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '400px'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚫</div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>Access Denied</h2>
            <p style={{ color: '#9ca3af', marginBottom: '1.5rem' }}>
              You need administrator privileges to access this dashboard.
            </p>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
              Current role: {user?.role || 'none'} | Required: admin
            </p>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#06b6d4',
                color: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Background Effects */}
      <div className={styles.backgroundEffects}>
        <div className={styles.backgroundOrb1}></div>
        <div className={styles.backgroundOrb2}></div>
        <div className={styles.backgroundOrb3}></div>
      </div>

      {/* Left Sidebar */}
      <div className={styles.sidebar}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.logoContainer}>
            <div className={styles.logoIcon}>🚀</div>
            <div>
              <div className={styles.logoText}>Kingdom Admin</div>
              <div className={styles.logoSubtext}>MISSION CONTROL</div>
            </div>
          </div>
          
          {user && (
            <div className={styles.userProfile}>
              <div className={styles.userAvatar}>{user.email?.[0]?.toUpperCase()}</div>
              <div className={styles.userInfo}>
                <div className={styles.userName}>{user.email}</div>
                <div className={styles.userRole}>Administrator</div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className={styles.navigation}>
          {navigationItems.map((item) => (
            <button 
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`${styles.navItem} ${activeTab === item.id ? styles.navItemActive : ''}`}
            >
              <div className={styles.navItemContent}>
                <div className={styles.navItemIcon}>{item.icon}</div>
                <div className={styles.navItemText}>
                  <div className={styles.navItemLabel}>{item.label}</div>
                  <div className={styles.navItemDescription}>{item.description}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Logout */}
        <div className={styles.logout}>
          <button onClick={handleLogout} className={styles.logoutButton}>
            <div className={styles.logoutIcon}>🚪</div>
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.contentArea}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default FuturisticWaitlistManager;